import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  disconnectInstagramUserProfile,
  getInstagramUserProfile,
} from '../../../hooks/instagram';
import { instagramAccountDetails } from '../../../utils/types';
import DisconnectChannelDialog from '../../../components/DisconnectChannelDialog';

export default function InstagramSettings() {
  const { orgId, profileId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [accountData, setAccountData] = useState<instagramAccountDetails | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    const loadAccount = async () => {
      setLoading(true);
      try {
        const response = await getInstagramUserProfile(orgId);
        if (response.success && Array.isArray(response.data) && response.data[0]) {
          setAccountData(response.data[0]);
        } else {
          setAccountData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [orgId]);

  const handleDisconnect = async () => {
    if (!orgId || !profileId) return;

    setDisconnecting(true);
    try {
      const response = await disconnectInstagramUserProfile(orgId, profileId);
      if (!response.success) {
        throw new Error('Disconnect failed');
      }

      toast({
        title: 'Instagram disconnected',
        description: 'The account has been fully disconnected from this organization.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(`/organization/inbox/instagram`);
    } catch (error) {
      toast({
        title: 'Disconnect failed',
        description: 'The account could not be disconnected.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDisconnecting(false);
      onClose();
    }
  };

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="#F7F4F8">
        <Spinner color="#C13584" size="xl" />
      </Flex>
    );
  }

  if (!accountData) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="#F7F4F8">
        <Text color="gray.500">No Instagram account connected.</Text>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="#F7F4F8" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <Box maxW="1100px" mx="auto">
        <Grid templateColumns={{ base: '1fr', lg: '1.1fr 0.9fr' }} gap={6}>
          <Box
            bg="white"
            borderRadius="32px"
            border="1px solid"
            borderColor="rgba(15, 23, 42, 0.08)"
            boxShadow="0 24px 64px rgba(15,23,42,0.08)"
            p={{ base: 6, md: 8 }}
          >
            <VStack align="stretch" spacing={6}>
              <Badge colorScheme="pink" borderRadius="full" px={3} py={1} w="fit-content">
                Connection status
              </Badge>
              <Box>
                <Heading size="lg" color="gray.900">
                  Instagram account settings
                </Heading>
                <Text mt={2} color="gray.500" lineHeight="1.8">
                  Review connection details, verify the active account, and
                  control how this Instagram workspace behaves for the current
                  organization.
                </Text>
              </Box>

              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                <SettingCard label="Username" value={`@${accountData.username || 'instagram'}`} />
                <SettingCard label="Account id" value={accountData.id} />
                <SettingCard label="AI chat responses" value="Available" />
                <SettingCard label="AI comment responses" value="Available" />
              </Grid>
            </VStack>
          </Box>

          <Box
            bg="white"
            borderRadius="32px"
            border="1px solid"
            borderColor="rgba(15, 23, 42, 0.08)"
            boxShadow="0 24px 64px rgba(15,23,42,0.08)"
            p={{ base: 6, md: 8 }}
            h="fit-content"
          >
            <VStack align="stretch" spacing={5}>
              <Box>
                <Heading size="md" color="gray.900">
                  Management actions
                </Heading>
                <Text mt={2} fontSize="sm" color="gray.500" lineHeight="1.8">
                  Keep disconnect as the only destructive action here. Other
                  account operations should remain read-only until explicitly
                  supported by the backend.
                </Text>
              </Box>
              <Divider />
              <Button
                colorScheme="pink"
                variant="outline"
                borderRadius="full"
                onClick={() =>
                  navigate(`/organization/${orgId}/inbox/instagram/conversations`)
                }
              >
                Return to inbox
              </Button>
              <Button
                colorScheme="red"
                borderRadius="full"
                onClick={onOpen}
                isLoading={disconnecting}
              >
                Disconnect account
              </Button>
            </VStack>
          </Box>
        </Grid>
      </Box>
      <DisconnectChannelDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDisconnect}
        title="Disconnect Instagram account?"
        body={`This will disconnect @${accountData.username || 'instagram'} from this organization and stop syncing new messages and comments.`}
        confirmLabel="Disconnect Instagram"
        isLoading={disconnecting}
      />
    </Box>
  );
}

const SettingCard = ({ label, value }: { label: string; value: string }) => (
  <Box
    border="1px solid"
    borderColor="rgba(15, 23, 42, 0.08)"
    borderRadius="24px"
    p={5}
    bg="gray.50"
  >
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={2}>
      {label}
    </Text>
    <Text color="gray.900" fontWeight="700" wordBreak="break-word">
      {value}
    </Text>
  </Box>
);
